import { Fragment, lazy, useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "./components/Icon";
import { Brand, Empty, Toast } from "./components/ui";
import { LanguagePicker } from "./components/LanguagePicker";
import { InstallApp } from "./components/InstallApp";
import { UrduBetaNotice } from "./components/UrduBetaNotice";
import { ParentHome } from "./components/Home";
import { TodayPage, momentsFor, splitMoments, type PlannedMoment } from "./components/Today";
import { CatchUpStrip } from "./components/CatchUp";
import { hasNewFeedback, readFeedbackSeen, writeFeedbackSeen } from "./lib/feedback";
import { fetchLearnerTeachers, fetchOpenTutorRequests, requestSubjectTutor } from "./lib/data";
import { blocksFor } from "./lib/pathways";
import { ActivityModal } from "./components/ActivityModal";
import { loadNarration, unlockNarration, type NarrationClip } from "./lib/narration";
import { Paywall, TrialBanner } from "./components/Subscription";
import { useAccount } from "./lib/useAccount";
import { canOpenPage, usePageNavigation, type Page } from "./lib/navigation";
import { LearnerForm } from "./components/LearnerForm";
import { Welcome } from "./components/Welcome";
import { Onboarding } from "./components/Onboarding";
import { NewPasswordPrompt } from "./components/NewPasswordPrompt";
import { Tour, familyTourSteps, rememberTour, type TourStep } from "./components/Tour";
import { useHomeschool } from "./lib/useHomeschool";
import { useI18n } from "./lib/i18n";
import { loginUrl } from "./lib/auth";
import { initialsFor } from "./lib/data";
import { isManagement, type Activity, type Competency } from "./types";

import { LazyPanel } from "./components/LazyPanel";
import type { CurriculumTeacher } from "./components/CurriculumOverview";

const MasteryPage = lazy(() => import("./components/MasteryMap").then(module => ({ default: module.MasteryPage })));
const PortfolioPage = lazy(() => import("./components/Portfolio").then(module => ({ default: module.PortfolioPage })));
const ReportsPage = lazy(() => import("./components/Reports").then(module => ({ default: module.ReportsPage })));
const CurriculumOverview = lazy(() => import("./components/CurriculumOverview").then(module => ({ default: module.CurriculumOverview })));
const TutorRequest = lazy(() => import("./components/TutorRequest").then(module => ({ default: module.TutorRequest })));
const EvidenceModal = lazy(() => import("./components/EvidenceModal").then(module => ({ default: module.EvidenceModal })));
const FamilyPage = lazy(() => import("./components/Family").then(module => ({ default: module.FamilyPage })));
const SuggestionsPage = lazy(() => import("./components/Suggestions").then(module => ({ default: module.SuggestionsPage })));
const AdminFamiliesPage = lazy(() => import("./components/AdminFamilies").then(module => ({ default: module.AdminFamiliesPage })));
const AdminMatching = lazy(() => import("./components/AdminMatching").then(module => ({ default: module.AdminMatching })));
const ReviewPage = lazy(() => import("./components/Review").then(module => ({ default: module.ReviewPage })));
const ActivityLibraryAdmin = lazy(() => import("./components/Admin").then(module => ({ default: module.ActivityLibraryAdmin })));
const CompetencyAdmin = lazy(() => import("./components/Admin").then(module => ({ default: module.CompetencyAdmin })));

/**
 * Page identifiers are stable ids, never the visible label — the label changes
 * with the language and would otherwise stop matching the active page the
 * moment a family switched to Urdu.
 */
const FAMILY_NAV: { id: Page; icon: string }[] = [
  { id: "home", icon: "home" },
  { id: "today", icon: "sun" },
  { id: "curriculum", icon: "map" },
  { id: "mastery", icon: "map" },
  { id: "portfolio", icon: "image" },
  { id: "reports", icon: "chart" },
  { id: "family", icon: "users" },
  { id: "subscription", icon: "star" },
  { id: "suggestions", icon: "message" },
];

const ADMIN_NAV: { id: Page; icon: string }[] = [
  { id: "activities", icon: "spark" },
  { id: "competencies", icon: "book" },
  { id: "learners", icon: "users" },
  // Above Review on purpose: a family waiting to be matched has nobody
  // teaching them yet, which is more urgent than a queue of evidence.
  { id: "matching", icon: "target" },
  { id: "review", icon: "shield" },
];

/** Teachers only see learners assigned to them by RLS, and only the parts of
 * the family record needed to prepare, teach and report. */
const TEACHER_NAV: { id: Page; icon: string }[] = [
  { id: "home", icon: "home" },
  { id: "today", icon: "sun" },
  { id: "curriculum", icon: "map" },
  { id: "mastery", icon: "target" },
  { id: "portfolio", icon: "image" },
  { id: "reports", icon: "chart" },
];

/**
 * Pages that stand on their own without a child selected.
 *
 * An admin has no children of their own and must still reach the programme
 * screens; a parent mid-way through adding their first child must still reach
 * the family page that lets them add one.
 */
const LEARNER_FREE_PAGES: Page[] = ["activities", "competencies", "learners", "matching", "review", "family", "subscription", "suggestions"];

/** A full-screen message for the states where there is no app to show. */
function Standalone({
  title,
  copy,
  action,
}: {
  title: string;
  copy: string;
  action?: { label: string; href?: string; onClick?: () => void };
}) {
  return (
    <main className="app-shell" style={{ gridTemplateColumns: "1fr" }}>
      <div className="ambient ambient-one" />
      <div className="ambient ambient-two" />
      <section className="workspace">
        <div className="content" style={{ display: "grid", placeItems: "center", minHeight: "100vh" }}>
          <div
            className="glass-card report-block"
            style={{ maxWidth: 480, textAlign: "center", alignItems: "center" }}
          >
            <Brand />
            <h3 style={{ marginTop: 18 }}>{title}</h3>
            <p>{copy}</p>
            {/* The picker belongs here as well as in the top bar: someone who
                needs Urdu needs it before they sign in, not after. */}
            <div style={{ display: "flex", justifyContent: "center", marginTop: 18 }}>
              <LanguagePicker />
            </div>
            {action &&
              (action.href ? (
                <a
                  className="primary"
                  href={action.href}
                  style={{ marginTop: 18, display: "inline-flex", textDecoration: "none" }}
                >
                  {action.label} <Icon name="arrow" />
                </a>
              ) : (
                <button className="primary" onClick={action.onClick} style={{ marginTop: 18 }}>
                  {action.label}
                </button>
              ))}
          </div>
        </div>
      </section>
    </main>
  );
}

export default function App() {
  const app = useHomeschool();
  const { t, locale } = useI18n();
  const [requestedPage, setPage] = usePageNavigation();
  const page = app.user && !canOpenPage(requestedPage, app.user.role) ? "home" : requestedPage;
  useEffect(() => {
    if (app.user && !canOpenPage(requestedPage, app.user.role)) setPage("home", true);
  }, [app.user, requestedPage, setPage]);
  const [mobileNav, setMobileNav] = useState(false);
  const [learnerMenu, setLearnerMenu] = useState(false);
  const [toast, setToast] = useState("");

  const [openMoment, setOpenMoment] = useState<PlannedMoment | null>(null);

  /**
   * Where this account stands, and whether a new day may be started.
   *
   * fetchAccountState also starts the free week if this account has never had
   * one — homeschool_ensure_trial, which can only ever write a trial and only
   * for the caller. That covers families who arrived through some other Al Noor
   * sign-up path and would otherwise have had no entitlement record at all.
   *
   * Optimistic until it answers: `access: true` while unknown, so a slow or
   * failed lookup never shows a paywall to somebody who has paid.
   */
  const account = useAccount(app.supabase, app.user?.id,
    app.user?.role === "parent" && app.learner?.learning_route === "parent_led");

  // Spend the first tap anywhere on the permission to play sound later. Passive
  // and once only; it makes no noise.
  useEffect(() => {
    const onFirst = () => unlockNarration();
    window.addEventListener("pointerdown", onFirst, { once: true, passive: true });
    window.addEventListener("keydown", onFirst, { once: true });
    return () => {
      window.removeEventListener("pointerdown", onFirst);
      window.removeEventListener("keydown", onFirst);
    };
  }, []);
  const [evidenceFor, setEvidenceFor] = useState<{ activity: Activity | null; planItemId: string | null } | null>(
    null,
  );
  const [addingLearner, setAddingLearner] = useState(false);
  const [portfolioFilter, setPortfolioFilter] = useState<string | null>(null);
  useEffect(() => {
    setMobileNav(false);
    setLearnerMenu(false);
    setOpenMoment(null);
    setEvidenceFor(null);
    setTutorSubject(null);
    setAddingLearner(false);
    if (page !== "portfolio") setPortfolioFilter(null);
  }, [page, app.learner?.id]);

  // Split here rather than in each page. `moments` keeps meaning "the day" for
  // every consumer that counts one — the progress ring, the done tally, the
  // print sheet — so an optional extra can never make a family read 6/8 and
  // conclude they are behind on a day they in fact finished.
  const { core: moments, extras } = useMemo(
    () => splitMoments(momentsFor(app.plan, app.activities)),
    [app.plan, app.activities],
  );
  const evidenceDates = useMemo(() => app.evidence.map((item) => item.occurred_at), [app.evidence]);
  // What the streak counts: a day with a tick or a piece of evidence.
  const streakDates = useMemo(() => [...app.workedDates, ...evidenceDates], [app.workedDates, evidenceDates]);
  // The earlier day's six, never its optional extras.
  const catchUpMoments = useMemo(
    () => splitMoments(momentsFor(app.catchUp, app.activities)).core,
    [app.catchUp, app.activities],
  );
  const [dismissedCatchUp, setDismissedCatchUp] = useState<string | null>(null);

  // Signed in advance, for the whole day at once.
  //
  // Signing when an activity opens would put an await between the parent's tap
  // and the play, and a browser only permits playback it can trace to a
  // gesture — so autoplay would work on a desktop and fail silently on the
  // phones families actually use. One batch when the plan loads means there is
  // nothing to wait for at open time.
  const [clips, setClips] = useState<Map<string, NarrationClip>>(new Map());
  useEffect(() => {
    const supabase = app.supabase;
    if (!supabase) return;
    const ids = [...moments, ...extras].map((moment) => moment.activity.id);
    if (ids.length === 0) return;
    let cancelled = false;
    void loadNarration(supabase, ids, locale).then((loaded) => {
      if (!cancelled) setClips(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, [app.supabase, moments, extras, locale]);


  /**
   * The child's level and its terms.
   *
   * UP HERE, above every early return, because that is where hooks have to
   * live. These were originally declared further down beside the other
   * derived values — which is after the component returns early while booting,
   * so React saw two extra hooks the moment boot finished, threw "rendered
   * more hooks than during the previous render", and unmounted the whole app
   * to a blank screen straight after the opening card.
   *
   * Falls back to the middle level of the child's old stage, the same rule the
   * plan generator uses, so a learner created before levels existed still gets
   * a progress line rather than a gap where it should be.
   */
  const learnerLevel = useMemo(() => {
    const current = app.learner;
    if (!current) return null;
    const slug = current.level_slug ?? (current.stage === "foundation" ? "nursery" : "class-1");
    return app.levels.find((level) => level.slug === slug) ?? null;
  }, [app.learner, app.levels]);

  /**
   * Who is teaching this child, for the curriculum screen.
   *
   * Only fetched for the supported routes — a parent-led family has no assigned
   * teacher and the request would be a round trip for an empty list on every
   * learner switch.
   */
  const [learnerTeachers, setLearnerTeachers] = useState<CurriculumTeacher[]>([]);
  /** The subject whose tutor-request sheet is open, if any. */
  const [tutorSubject, setTutorSubject] = useState<string | null>(null);
  /** Subjects this child already has an open request for. */
  const [requestedSubjects, setRequestedSubjects] = useState<Set<string>>(new Set());
  const learnerId = app.learner?.id;
  const viewerId = app.user?.id;

  /**
   * Feedback this guardian has not read yet.
   *
   * "Seen" is stamped on the way INTO the portfolio; the value from before that
   * visit is kept so the page can still mark which notes are new while the
   * parent is reading them.
   */
  const [feedbackSeen, setFeedbackSeen] = useState<string | null>(null);
  const [feedbackSeenBefore, setFeedbackSeenBefore] = useState<string | null>(null);
  useEffect(() => {
    setFeedbackSeen(readFeedbackSeen(viewerId, learnerId));
  }, [viewerId, learnerId]);
  useEffect(() => {
    if (page !== "portfolio" || !viewerId || !learnerId) return;
    const now = new Date().toISOString();
    setFeedbackSeenBefore(readFeedbackSeen(viewerId, learnerId));
    writeFeedbackSeen(viewerId, learnerId, now);
    setFeedbackSeen(now);
  }, [page, viewerId, learnerId]);
  const learnerRoute = app.learner?.learning_route;
  const learnerSubjects = app.learner?.supported_subjects;
  const supabaseClient = app.supabase;
  useEffect(() => {
    if (!supabaseClient || !learnerId || !learnerRoute || learnerRoute === "parent_led") {
      setLearnerTeachers([]);
      return;
    }
    let cancelled = false;
    void fetchLearnerTeachers(supabaseClient, learnerId)
      .then((rows) => {
        if (cancelled) return;
        setLearnerTeachers(
          rows.map((row) => ({
            id: row.id,
            // Families cannot read a teacher's profile, so the name is often
            // absent by design. Say what is true rather than showing a blank.
            name: row.name ?? "Assigned teacher",
            // On tutor support the family named the subjects, so the tutors on
            // the child are the ones covering them.
            // The subjects this family asked for help with, which is what a
            // tutor on this child is there to cover.
            subjects: learnerSubjects ?? [],
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setLearnerTeachers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [supabaseClient, learnerId, learnerRoute, learnerSubjects]);

  /**
   * Which subjects already have a tutor request open.
   *
   * Read so the button on a subject row can say "requested" instead of
   * inviting a second ask. Reloaded after a successful request rather than
   * guessed at locally, so two devices agree.
   */
  const [requestNonce, setRequestNonce] = useState(0);
  useEffect(() => {
    if (!supabaseClient || !learnerId) {
      setRequestedSubjects(new Set());
      return;
    }
    let cancelled = false;
    void fetchOpenTutorRequests(supabaseClient, learnerId)
      .then((rows) => {
        if (!cancelled) setRequestedSubjects(new Set(rows.map((row) => row.subject)));
      })
      .catch(() => {
        if (!cancelled) setRequestedSubjects(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [supabaseClient, learnerId, requestNonce]);
  /**
   * Hold the page still while the mobile drawer is open.
   *
   * Without this a drag inside the drawer scrolled the page underneath it —
   * the drawer looked frozen and the content behind slid away. Hiding body
   * overflow is most of the fix; pinning the scroll position is the rest,
   * because iOS Safari rubber-bands a body that is merely overflow:hidden and
   * loses your place when the drawer closes.
   *
   * Above the early returns, with the other hooks. This component returns early
   * four times while booting, and a hook below that point changes the hook
   * count between renders and unmounts the whole app.
   */
  useEffect(() => {
    if (!mobileNav) return;
    const { body } = document;
    const scrollY = window.scrollY;
    body.classList.add("nav-open");
    body.style.top = `-${scrollY}px`;
    body.style.position = "fixed";
    body.style.width = "100%";
    return () => {
      body.classList.remove("nav-open");
      body.style.removeProperty("top");
      body.style.removeProperty("position");
      body.style.removeProperty("width");
      window.scrollTo(0, scrollY);
    };
  }, [mobileNav]);

  const learnerTerms = useMemo(
    () => (learnerLevel ? app.terms.filter((term) => term.level_id === learnerLevel.id) : []),
    [learnerLevel, app.terms],
  );

  /**
   * The walk round the app, opened straight after the first child is added
   * and again from the menu whenever a family asks for it.
   *
   * Up here with the other hooks, above every early return. The steps are
   * memoised because the tour re-measures its target whenever a step object
   * changes, and a fresh array each render would re-scroll the page each render.
   */
  const [tourOpen, setTourOpen] = useState(false);
  const guardianFirst = app.user?.name.trim().split(/\s+/)[0] ?? "";
  const childFirst = app.learner?.name.trim().split(/\s+/)[0] ?? "";
  const tourSteps = useMemo(
    () => familyTourSteps(t, { guardian: guardianFirst, child: childFirst }),
    [t, guardianFirst, childFirst],
  );
  // On a phone the sidebar is a drawer: open it for the steps that live there.
  const onTourStep = useCallback((step: TourStep) => {
    const phone = window.matchMedia("(max-width: 780px)").matches;
    setMobileNav(phone && Boolean(step.inNav));
  }, []);
  const tourUserId = app.user?.id;
  const closeTour = useCallback(
    (finished: boolean) => {
      setTourOpen(false);
      setMobileNav(false);
      if (tourUserId) rememberTour(tourUserId, finished);
    },
    [tourUserId],
  );

  function notify(message: string) {
    setToast(message);
    window.setTimeout(() => setToast(""), 2600);
  }

  function goToPage(next: Page) {
    setPage(next);
    setMobileNav(false);
    if (next !== "portfolio") setPortfolioFilter(null);
  }

  function viewCompetencyEvidence(competency: Competency) {
    setPortfolioFilter(competency.id);
    setPage("portfolio");
  }

  if (app.boot === "loading" || (app.boot === "ready" && app.familyLoading)) {
    return <Standalone title={t("boot.loading")} copy={t("boot.loadingHint")} />;
  }

  if (app.boot === "unavailable") {
    return <Standalone title={t("boot.unavailable")} copy={t("boot.unavailableHint")}
      action={{ label: t("error.retry"), onClick: () => window.location.reload() }} />;
  }

  if (app.boot === "signed-out" || !app.user || !app.supabase) {
    return (
      <Welcome
        signInHref={loginUrl()}
        supabase={app.supabase}
        // The auth listener only watches for signing out, so a fresh session
        // is handed over explicitly — straight into the curriculum choice with
        // no page reload. The reload stays as the fallback if that handover
        // cannot finish, since it rebuilds everything from the cookie.
        onSignedIn={async () => {
          if (!(await app.completeSignIn())) window.location.reload();
        }}
      />
    );
  }

  const management = isManagement(app.user.role);
  const teacher = app.user.role === "teacher";
  const nav = management ? [...FAMILY_NAV, ...ADMIN_NAV] : teacher ? TEACHER_NAV : FAMILY_NAV;
  const learner = app.learner;
  // Who may record for this child: its own guardian, or the programme. A coach
  // can read the day but not tick it, and RLS would refuse if they tried.
  const canEdit = management || (!!learner && learner.guardian_id === app.user.id);
  const catchUpId = app.catchUp?.id ?? null;
  // Only the child's own guardian is told about new feedback; the reviewer
  // who wrote it does not need a dot for their own note.
  const newFeedback = !!learner && learner.guardian_id === app.user.id && hasNewFeedback(app.evidence, feedbackSeen);
  const showCatchUp = canEdit && !!catchUpId && dismissedCatchUp !== catchUpId && !catchUpDismissed(catchUpId);

  if (app.familyError) {
    return <Standalone title={t("error.title")} copy={app.familyError}
      action={{ label: t("error.retry"), onClick: () => void app.reloadFamily() }} />;
  }

  // A teacher never creates a child. The assignment is made by the programme,
  // and homeschool learner RLS decides which children appear in this account.
  if (!learner && teacher) {
    return (
      <Standalone
        title="No learner assigned yet"
        copy="Your teaching workspace will appear here when Al Noor assigns a family and confirms the timetable."
      />
    );
  }

  // A guardian with no children yet cannot be shown a dashboard about nobody:
  // they choose a curriculum and tell us who the children are, then land on
  // the first child's day with the tour open.
  if (!learner && !management && !teacher) {
    return (
      <>
        <Onboarding
          guardianName={app.user.name}
          levels={app.levels}
          onCreateChild={app.createChild}
          onFinished={(firstLearnerId) => {
            app.setLearnerId(firstLearnerId);
            setTourOpen(true);
          }}
          onSignOut={() => void app.signOut()}
        />
        <NewPasswordPrompt supabase={app.supabase} onSaved={notify} />
        {toast && <Toast message={toast} />}
      </>
    );
  }

  return (
    <main className="app-shell">
      <div className="ambient ambient-one" />
      <div className="ambient ambient-two" />
      <div className="ambient ambient-three" />

      <aside className={`sidebar glass ${mobileNav ? "open" : ""}`}>
        <div className="side-head">
          <Brand />
          <button
            className="icon-button mobile-only"
            onClick={() => setMobileNav(false)}
            aria-label={t("top.closeNav")}
          >
            <Icon name="close" />
          </button>
        </div>
        <nav aria-label={t("nav.main")}>
          <p className="eyebrow">{t("nav.familySection")}</p>
          {nav.map((item) => (
            <Fragment key={item.id}>
            {item.id === "activities" && <p className="eyebrow nav-section">{t("nav.programme")}</p>}
            <button
              className={page === item.id ? "active" : ""}
              aria-current={page === item.id ? "page" : undefined}
              onClick={() => goToPage(item.id)}
              data-tour={`nav-${item.id}`}
            >
              <span>
                <Icon name={item.icon} />
              </span>
              {t(`nav.${item.id}`)}
              {item.id === "portfolio" && newFeedback && page !== "portfolio" && (
                <span className="nav-dot" role="img" aria-label={t("nav.newFeedback")} />
              )}
              {page === item.id && <i />}
            </button>
            </Fragment>
          ))}
        </nav>
        <div className="side-quote">
          <Icon name="spark" size={16} />
          <p>{t("side.quote")}</p>
          <small>{app.user.name}</small>
        </div>
        {!teacher && (
          <button
            className="help-link"
            onClick={() => {
              setMobileNav(false);
              setTourOpen(true);
            }}
          >
            <Icon name="spark" /> {t("tour.replay")}
          </button>
        )}
        <button className="help-link" onClick={() => void app.signOut()}>
          <Icon name="arrow" /> {t("side.signOut")}
        </button>
        <InstallApp />
      </aside>

      <section className="workspace">
        <header className="topbar glass">
          <button
            className="icon-button mobile-only"
            onClick={() => setMobileNav(true)}
            aria-label={t("top.openNav")}
          >
            <Icon name="menu" />
          </button>

          {learner ? (
            <div className="popover-wrap">
              <div className="learner-switch" data-tour="learner">
                <div className="avatar">{learner.initials || initialsFor(learner.name)}</div>
                <div>
                  <span>{t("top.learningWith")}</span>
                  <strong>{learner.name}</strong>
                </div>
                <button onClick={() => setLearnerMenu(!learnerMenu)} aria-label={t("top.changeLearner")} aria-expanded={learnerMenu}>
                  <Icon name="chevron" size={15} />
                </button>
              </div>
              {learnerMenu && (
                <div className="popover role-menu glass">
                  {app.learners.map((candidate) => (
                    <button
                      key={candidate.id}
                      className={candidate.id === learner.id ? "selected" : ""}
                      onClick={() => {
                        app.setLearnerId(candidate.id);
                        setLearnerMenu(false);
                      }}
                    >
                      <span className={`role-dot ${candidate.avatar_color}`}>
                        {candidate.initials || initialsFor(candidate.name)}
                      </span>
                      <div>
                        <strong>{candidate.name}</strong>
                        <small>{t(`stage.${candidate.stage}`)}</small>
                      </div>
                      {candidate.id === learner.id && <Icon name="check" />}
                    </button>
                  ))}
                  {!teacher ? (
                    <button
                      onClick={() => {
                        setLearnerMenu(false);
                        setAddingLearner(true);
                      }}
                    >
                      <span className="role-dot">
                        <Icon name="plus" size={14} />
                      </span>
                      <div>
                        <strong>{t("top.addLearner")}</strong>
                        <small>{t("top.addLearnerHint")}</small>
                      </div>
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          ) : (
            <div className="learner-switch">
              <div>
                <span>{t("top.signedInAs")}</span>
                <strong>{app.user.name}</strong>
              </div>
            </div>
          )}

          <div className="top-actions">
            <LanguagePicker tourId="language" />
            {!teacher ? (
              <button
                className="primary"
                onClick={() => setEvidenceFor({ activity: null, planItemId: null })}
                disabled={!learner}
                data-tour="record"
              >
                <Icon name="camera" /> {t("top.recordEvidence")}
              </button>
            ) : null}
          </div>
        </header>

        <div className="content">
          <UrduBetaNotice onSuggest={() => goToPage("suggestions")} />
          <TrialBanner account={account} onUpgrade={() => goToPage("subscription")} />
          {!account.access && page !== "today" && page !== "subscription" && (
            <div className="trial-banner urgent"><p>{t("paywall.title")}</p>
              <button className="soft-button" onClick={() => goToPage("subscription")}>{t("trial.keepGoing")}</button>
            </div>
          )}
          {learner && learner.learning_route !== "parent_led" && !teacher ? (
            <div className="insight-strip glass-card">
              <span className="icon-tile gold">
                <Icon name="users" />
              </span>
              <div>
                <span className="eyebrow">{t(`enrol.route.${learner.learning_route}.title`)}</span>
                <p>
                  {/* The pathway is named from the dictionary, not by tidying
                      up the stored value: `future_skills` became "future
                      skills" in every language, which is neither the name of
                      the pathway nor Urdu. */}
                  {t("home.requestSent", {
                    pathway: learner.curriculum_pathway
                      ? t(`enrol.pathway.${learner.curriculum_pathway}.title`)
                      : "",
                  })}
                </p>
              </div>
              <a className="soft-button" href="https://www.alnoortuition.com/teachers">
                {t("home.viewTeachers")}
              </a>
            </div>
          ) : null}

          {app.error && (
            <div className="insight-strip glass-card">
              <span className="icon-tile rose">
                <Icon name="shield" />
              </span>
              <div>
                <span className="eyebrow">{t("error.title")}</span>
                <p>{app.error}</p>
              </div>
              <button onClick={() => void app.reloadLearner()}>
                {t("error.retry")} <Icon name="arrow" />
              </button>
            </div>
          )}

          <LazyPanel key={page}>
          {page === "subscription" && (
            <Paywall supabase={app.supabase} account={account} learnerName={learner?.name} />
          )}
          {page === "activities" && management && (
            <ActivityLibraryAdmin
              supabase={app.supabase}
              activities={app.activities}
              competencies={app.competencies}
              levels={app.levels}
              userId={app.user.id}
              onChanged={app.reloadFamily}
            />
          )}

          {page === "competencies" && management && (
            <CompetencyAdmin
              supabase={app.supabase}
              competencies={app.competencies}
              onChanged={app.reloadFamily}
            />
          )}

          {page === "learners" && management && (
            <AdminFamiliesPage supabase={app.supabase} userId={app.user.id} />
          )}

          {page === "matching" && management && <AdminMatching supabase={app.supabase} />}

          {page === "review" && management && (
            <ReviewPage
              supabase={app.supabase}
              userId={app.user.id}
              competencies={app.competencies}
            />
          )}

          {page === "family" && (
            <FamilyPage
              levels={app.levels}
              supabase={app.supabase}
              userId={app.user.id}
              guardianName={app.user.name}
              learners={app.learners}
              learner={learner}
              onSelectLearner={app.setLearnerId}
              onAddLearner={() => setAddingLearner(true)}
              onSaveLearner={async (learnerId, input) => {
                await app.saveLearner(learnerId, input);
                notify(t("learner.profileSaved"));
              }}
              onArchiveLearner={async (learnerId) => {
                await app.removeLearner(learnerId);
                notify(t("family.archived"));
              }}
            />
          )}

          {page === "suggestions" && (
            <SuggestionsPage
              supabase={app.supabase}
              userId={app.user.id}
              management={management}
              learners={app.learners}
              activities={app.activities}
            />
          )}

          {!learner && !LEARNER_FREE_PAGES.includes(page) && (
            <Empty
              icon="users"
              title={t("learner.noneTitle")}
              copy={t("learner.noneCopy")}
              action={
                <button className="primary" onClick={() => setAddingLearner(true)} style={{ marginTop: 16 }}>
                  <Icon name="plus" /> {t("top.addLearner")}
                </button>
              }
            />
          )}

          {learner && page === "home" && (
            <ParentHome
              guardianName={app.user.name}
              learnerName={learner.name}
              moments={moments}
              competencies={app.competencies}
              mastery={app.mastery}
              streakDates={streakDates}
              route={learner.learning_route}
              catchUp={showCatchUp && app.catchUp ? (
                <CatchUpStrip
                  learnerName={learner.name}
                  day={app.catchUp.plan_date}
                  today={app.date}
                  moments={catchUpMoments}
                  onToggle={(itemId) => void app.togglePastItem(app.catchUp!.id, itemId)}
                  onDismiss={() => {
                    rememberCatchUpDismissed(app.catchUp!.id);
                    setDismissedCatchUp(app.catchUp!.id);
                  }}
                />
              ) : null}
              feedback={newFeedback ? (
                <div className="feedback-strip">
                  <span className="icon-tile rose"><Icon name="message" /></span>
                  <div>
                    <strong>{t("home.feedbackTitle", { name: learner.name })}</strong>
                    <p>{t("home.feedbackCopy")}</p>
                  </div>
                  <button className="soft-button" onClick={() => goToPage("portfolio")}>
                    {t("home.feedbackOpen")} <Icon name="arrow" />
                  </button>
                </div>
              ) : null}
              onOpen={setOpenMoment}
              onEvidence={canEdit ? (moment) =>
                setEvidenceFor({ activity: moment.activity, planItemId: moment.item.id })
                : undefined}
              onToggle={canEdit ? (itemId) => void app.toggleItem(itemId) : undefined}
              onGoToMastery={() => goToPage("mastery")}
            />
          )}

          {learner && page === "today" && (
            <>
              {/* Above the day, not over it. A family inside their free week is
                  doing exactly what was asked of them, and a modal every
                  morning is how a good first week becomes a cancelled one. */}
              {account.access || app.plan ? (
                <TodayPage
                  learnerName={learner.name}
                  moments={moments}
                  extras={extras}
                  level={learnerLevel}
                  terms={learnerTerms}
                  daysWorked={app.daysWorked}
                  onOpen={setOpenMoment}
                  onToggle={canEdit ? (itemId) => void app.toggleItem(itemId) : undefined}
                />
              ) : (
                // Only the day is withheld. Portfolio, mastery, reports and the
                // curriculum all still open from the nav — the record is not a
                // hostage, and it is also the best argument for paying.
                <Paywall supabase={app.supabase} account={account} learnerName={learner.name} />
              )}
            </>
          )}

          {learner && page === "curriculum" && (
            <CurriculumOverview
              learnerName={learner.name}
              level={learnerLevel}
              terms={learnerTerms}
              activities={app.activities}
              daysWorked={app.daysWorked}
              pathway={learner.curriculum_pathway}
              route={learner.learning_route}
              teachers={learnerTeachers}
              blocks={blocksFor(learner)}
              requestedSubjects={requestedSubjects}
              // Staff and teachers see the same screen but do not ask for
              // tutors on a family's behalf; only a guardian may.
              onRequestTutor={app.user.role === "parent" ? setTutorSubject : undefined}
            />
          )}

          {learner && page === "mastery" && (
            <MasteryPage
              learnerName={learner.name}
              learnerStage={learner.stage}
              competencies={app.competencies}
              mastery={app.mastery}
              onViewEvidence={viewCompetencyEvidence}
            />
          )}

          {learner && page === "portfolio" && (
            <PortfolioPage
              supabase={app.supabase}
              learnerName={learner.name}
              evidence={app.evidence}
              competencies={app.competencies}
              highlightCompetencyId={portfolioFilter}
              feedbackSeenBefore={feedbackSeenBefore}
              onClearHighlight={() => setPortfolioFilter(null)}
              onAdd={() => setEvidenceFor({ activity: null, planItemId: null })}
              onDelete={async (item) => {
                await app.removeEvidence(item);
                notify(t("evidence.removed"));
              }}
            />
          )}

          {learner && page === "reports" && (
            <ReportsPage
              learnerName={learner.name}
              competencies={app.competencies}
              mastery={app.mastery}
              evidence={app.evidence}
            />
          )}
          </LazyPanel>
        </div>
      </section>

      <LazyPanel>
      {openMoment && learner && (
        <ActivityModal
          activity={openMoment.activity}
          moment={openMoment}
          clip={clips.get(openMoment.activity.id)}
          learnerName={learner.name}
          supabase={app.supabase}
          onClose={() => setOpenMoment(null)}
          onComplete={canEdit ? () => {
            void app.toggleItem(openMoment.item.id);
            setOpenMoment(null);
            notify(t("guide.updated"));
          } : undefined}
          onEvidence={canEdit ? () => {
            setEvidenceFor({ activity: openMoment.activity, planItemId: openMoment.item.id });
            setOpenMoment(null);
          } : undefined}
        />
      )}

      {evidenceFor && learner && (
        <EvidenceModal
          activity={evidenceFor.activity}
          planItemId={evidenceFor.planItemId}
          learnerName={learner.name}
          competencies={app.competencies}
          onClose={() => setEvidenceFor(null)}
          onSave={async (input) => {
            await app.saveEvidence(input);
            notify(t("evidence.saved", { name: learner.name }));
          }}
        />
      )}

      {addingLearner && (
        <LearnerForm
          levels={app.levels}
          firstChild={false}
          onClose={() => setAddingLearner(false)}
          onSave={async (input) => {
            await app.addLearner(input);
            notify(t("learner.ready"));
          }}
        />
      )}

      {tutorSubject && learner && app.supabase && (
        <TutorRequest
          subject={tutorSubject}
          onClose={() => setTutorSubject(null)}
          onSubmit={async (delivery) => {
            const result = await requestSubjectTutor(app.supabase!, learner, {
              subject: tutorSubject,
              delivery,
            });
            if (result.ok) {
              // Re-read rather than assume: the button's "requested" state has
              // to match what the office can actually see.
              setRequestNonce((value) => value + 1);
              notify(t("curriculum.requestSent", { subject: tutorSubject }));
            }
            return result.ok ? { ok: true } : { ok: false, error: result.error };
          }}
        />
      )}

      </LazyPanel>
      {tourOpen && learner && <Tour steps={tourSteps} onClose={closeTour} onStep={onTourStep} />}
      <NewPasswordPrompt supabase={app.supabase} onSaved={notify} />
      {toast && <Toast message={toast} />}
      <div className={`mobile-overlay ${mobileNav ? "show" : ""}`} onClick={() => setMobileNav(false)} />
    </main>
  );
}

/** "Not this time" is remembered per day, on this device, and never asked again. */
function catchUpDismissed(planDayId: string) {
  try { return localStorage.getItem(`catchup-dismissed:${planDayId}`) === "1"; } catch { return false; }
}

function rememberCatchUpDismissed(planDayId: string) {
  try { localStorage.setItem(`catchup-dismissed:${planDayId}`, "1"); } catch { /* Storage is optional. */ }
}
